/**
 * Exchange rates for display (rate_to_gbp = units of currency per 1 GBP).
 * Matches server convert_currency_amount and currency_rates.rate_to_gbp.
 */
import { getApiUrl } from './api-url-helper';
export const SUPPORTED_DISPLAY_CURRENCIES = ['GBP', 'USD', 'EUR', 'CAD', 'AUD'];
export function normalizeDisplayCurrency(code, fallback = 'GBP') {
    const u = (code || fallback).toUpperCase();
    if (SUPPORTED_DISPLAY_CURRENCIES.includes(u))
        return u;
    return fallback;
}
/** Stale fallback if API fails or a code is missing (aligned with prior hardcoded UI maps). */
export const FALLBACK_RATES_TO_GBP = {
    GBP: 1.0,
    USD: 1.27,
    EUR: 1.17,
    CAD: 1.72,
    AUD: 1.94
};
const STORAGE_KEY = 'mypace_exchange_rates_v1';
const TTL_MS = 6 * 60 * 60 * 1000;
function mergeWithFallback(rates) {
    return { ...FALLBACK_RATES_TO_GBP, ...rates };
}
/**
 * Convert amount using the same formula as SQL convert_currency_amount:
 * (amount / rate_to_gbp[from]) * rate_to_gbp[to]
 */
export function convertUsingRateToGbp(amount, fromCurrency, toCurrency, rates) {
    const from = (fromCurrency || 'GBP').toUpperCase();
    const to = (toCurrency || 'GBP').toUpperCase();
    if (from === to) {
        return Math.round(amount * 100) / 100;
    }
    const fromRate = rates[from] ?? FALLBACK_RATES_TO_GBP[from] ?? 1;
    const toRate = rates[to] ?? FALLBACK_RATES_TO_GBP[to] ?? 1;
    if (!Number.isFinite(fromRate) ||
        !Number.isFinite(toRate) ||
        fromRate <= 0 ||
        toRate <= 0) {
        return Math.round(amount * 100) / 100;
    }
    const result = (amount / fromRate) * toRate;
    return Math.round(result * 100) / 100;
}
export function readCachedPublicRates() {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
        return null;
    }
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (!parsed.rates || typeof parsed.fetchedAt !== 'number')
            return null;
        if (Date.now() - parsed.fetchedAt > TTL_MS)
            return null;
        return { rates: mergeWithFallback(parsed.rates), fresh: true };
    }
    catch {
        return null;
    }
}
export function clearPublicRatesCache() {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
        return;
    }
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    }
    catch {
        /* ignore */
    }
}
export function writeCachedPublicRates(rates) {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
        return;
    }
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ rates, fetchedAt: Date.now() }));
    }
    catch {
        /* ignore quota */
    }
}
export async function fetchPublicExchangeRates() {
    const url = getApiUrl('/location/exchange-rates-public');
    // omit: anonymous public endpoint; avoids credentialed CORS when API is on another origin
    const res = await fetch(url, { method: 'GET', credentials: 'omit', mode: 'cors' });
    if (!res.ok) {
        throw new Error(`Exchange rates request failed: ${res.status}`);
    }
    const body = (await res.json());
    if (!body.success || !body.data?.rates) {
        throw new Error('Invalid exchange rates response');
    }
    const rates = mergeWithFallback(body.data.rates);
    writeCachedPublicRates(body.data.rates);
    return {
        rates,
        last_updated: body.data.last_updated ?? null
    };
}
